/**
 * Hook for disconnecting with confirmation when tabs are open
 *
 * Provides disconnect functionality that warns users before closing tabs.
 * If no tabs exist for the connection, disconnect happens immediately.
 */
import { useState, useCallback, useRef } from 'react';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useConnectionStore } from '../store/connectionStore';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import { useSessionStore } from '../../sessions/store/sessionStore';

interface AffectedTab {
  id: string;
  title: string;
}

interface DisconnectDialogState {
  connectionId: string;
  connectionName: string;
  affectedTabs: AffectedTab[];
}

export function useDisconnectWithConfirmation() {
  const tabs = useWorkspaceStore((state) => state.tabs);
  const removeTab = useWorkspaceStore((state) => state.removeTab);
  const disconnectFromDatabase = useConnectionStore((state) => state.disconnectFromDatabase);
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const removeConnection = useDatabaseStore((state) => state.removeConnection);

  const [dialogState, setDialogState] = useState<DisconnectDialogState | null>(null);

  // Store pending callback for dialog-based disconnects
  const pendingOnComplete = useRef<
    ((result: { success: boolean; shouldShowConnectionsDialog: boolean }) => void) | undefined
  >();

  // Tabs that go away with the connection — the ones this hook closes, and the
  // ones it warns about first.
  //
  // Reading `connectionInfo` here is DELIBERATE and is the one place that does
  // NOT use `tabConnectionIds`: Schema/Data Compare and Database Blueprint tabs
  // keep their connections in `content` and are meant to survive a disconnect.
  // They fall back to the amber connection banner, which offers a Connect
  // button per endpoint (`useActiveTabConnectionNeed`, `Docs/ConnectionBanner.md`)
  // — half a compare is still worth keeping, and reconnecting is one click.
  // Everything else is unusable without its single connection, so it closes.
  // Pinned by `useDisconnectWithConfirmation.test.ts`; do not "complete" the
  // tabConnectionIds migration here.
  const getTabsForConnection = useCallback(
    (connectionId: string): AffectedTab[] => {
      return tabs
        .filter((tab) => tab.connectionInfo?.connectionId === connectionId)
        .map((tab) => ({ id: tab.id, title: tab.title }));
    },
    [tabs]
  );

  // Perform the actual disconnect (closes tabs if any, then disconnects)
  const performDisconnect = useCallback(
    async (connectionId: string) => {
      // Close all tabs for this connection
      const affectedTabs = getTabsForConnection(connectionId);
      for (const tab of affectedTabs) {
        removeTab(tab.id);
      }

      // Disconnect from the database
      const success = await disconnectFromDatabase(connectionId);
      if (success) {
        // Remove the connection from the explorer tree
        removeConnection(connectionId);
        // Explicit user intent: stop carrying this connection in the session.
        // Recorded only AFTER the disconnect resolves, so a save racing the
        // in-flight disconnect can't re-add it from `activeConnections`.
        useSessionStore.getState().removeConnectionFromSession(connectionId);
      }

      return success;
    },
    [getTabsForConnection, removeTab, disconnectFromDatabase, removeConnection]
  );

  // Initiate disconnect - either directly or with confirmation
  // onComplete callback receives: { success: boolean, shouldShowConnectionsDialog: boolean }
  const initiateDisconnect = useCallback(
    async (
      connectionId: string,
      connectionName: string,
      onComplete?: (result: { success: boolean; shouldShowConnectionsDialog: boolean }) => void
    ) => {
      const affectedTabs = getTabsForConnection(connectionId);

      if (affectedTabs.length === 0) {
        // No tabs to close, disconnect immediately
        const shouldShowDialog = activeConnections.length === 1;
        const success = await performDisconnect(connectionId);
        onComplete?.({ success, shouldShowConnectionsDialog: shouldShowDialog });
      } else {
        // Show confirmation dialog - store the callback for later
        setDialogState({ connectionId, connectionName, affectedTabs });
        // Store onComplete in a ref so it can be called when dialog is confirmed
        pendingOnComplete.current = onComplete;
      }
    },
    [getTabsForConnection, activeConnections.length, performDisconnect]
  );

  // User confirmed disconnect
  const confirmDisconnect = useCallback(async () => {
    if (!dialogState) return false;

    // Calculate before disconnect (when there's 1 connection, after disconnect there will be 0)
    const shouldShowDialog = activeConnections.length === 1;
    const success = await performDisconnect(dialogState.connectionId);
    setDialogState(null);

    // Call pending callback if it exists
    const callback = pendingOnComplete.current;
    pendingOnComplete.current = undefined;
    callback?.({ success, shouldShowConnectionsDialog: shouldShowDialog });

    return success;
  }, [dialogState, performDisconnect, activeConnections.length]);

  // User cancelled disconnect
  const cancelDisconnect = useCallback(() => {
    setDialogState(null);
    pendingOnComplete.current = undefined;
  }, []);

  // Check if we need to show the connections dialog after disconnect
  const shouldShowConnectionsDialog = useCallback(() => {
    return activeConnections.length === 1;
  }, [activeConnections.length]);

  return {
    dialogState,
    initiateDisconnect,
    confirmDisconnect,
    cancelDisconnect,
    shouldShowConnectionsDialog,
  };
}
